import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Import customer actions
import { getWishlistItemsCount } from './store/customerSlice';
// Import cart actions
import { loadUserCart } from './store/slices/cartSlice';

// Import components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/layout/ScrollToTop';
import LoadingScreen from './components/layout/LoadingScreen';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import ProfilePage from './pages/ProfilePage';
import WishlistPage from './pages/WishlistPage';
import OrdersPage from './pages/OrdersPage';
import OrderManagement from './pages/OrderManagement';
import SupportPage from './pages/SupportPage';
import AboutUsPage from './pages/AboutUsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ContactUsPage from './pages/ContactUsPage';
import OurStoryPage from './pages/OurStoryPage';
import CareersPage from './pages/CareersPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import SitemapPage from './pages/SitemapPage';
import ReturnRefund from './pages/ReturnRefund';
import CustomSol from './pages/CustomSol';
import Quality from './pages/Quality';
import AdminDashboard from './pages/admin/AdminDashboard';
import SellerDashboard from './pages/seller/SellerDashboard';
import NotFoundPage from './pages/NotFoundPage';
import DeleteAccountPage from './pages/DeleteAccountPage';

// Import store
import store from './store/store';
import auditService from './services/auditService';

// Create theme for building materials marketplace
const theme = createTheme({
  palette: {
    primary: {
      main: '#2C3E50', // Dark blue-gray for professional look
      light: '#34495E',
      dark: '#1A252F',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#E67E22', // Warm orange for accent
      light: '#F39C12',
      dark: '#D35400',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F8F9FA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2C3E50',
      secondary: '#7F8C8D',
    },
    success: {
      main: '#27AE60',
      light: '#2ECC71',
      dark: '#229954',
    },
    warning: {
      main: '#F39C12',
      light: '#F1C40F',
      dark: '#E67E22',
    },
    error: {
      main: '#E74C3C',
      light: '#EC7063',
      dark: '#C0392B',
    },
    info: {
      main: '#3498DB',
      light: '#5DADE2',
      dark: '#2980B9',
    },
  },
  typography: {
    fontFamily: '"Inter", "SF Pro Display", "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    fontWeightExtraBold: 700,
    h1: {
      fontSize: '3rem',
      fontWeight: 700,
      lineHeight: 1.167,
      letterSpacing: '-0.01562em',
      color: '#2C3E50',
    },
    h2: {
      fontSize: '2.25rem',
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: '-0.00833em',
      color: '#2C3E50',
    },
    h3: {
      fontSize: '1.875rem',
      fontWeight: 600,
      lineHeight: 1.267,
      letterSpacing: '0em',
      color: '#2C3E50',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.375,
      letterSpacing: '0.00735em',
      color: '#2C3E50',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0em',
      color: '#2C3E50',
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.444,
      letterSpacing: '0.0075em',
      color: '#2C3E50',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: '0.00938em',
      color: '#2C3E50',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.429,
      letterSpacing: '0.00714em',
      color: '#2C3E50',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: '0.00938em',
      color: '#2C3E50',
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.571,
      letterSpacing: '0.00714em',
      color: '#7F8C8D',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.03333em',
      color: '#95A5A6',
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 500,
      lineHeight: 2,
      letterSpacing: '0.08333em',
      textTransform: 'uppercase',
      color: '#95A5A6',
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.429,
      letterSpacing: '0.02857em',
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.1)',
    '0px 4px 8px rgba(0,0,0,0.1)',
    '0px 8px 16px rgba(0,0,0,0.1)',
    '0px 12px 24px rgba(0,0,0,0.1)',
    '0px 16px 32px rgba(0,0,0,0.1)',
    '0px 20px 40px rgba(0,0,0,0.1)',
    '0px 24px 48px rgba(0,0,0,0.1)',
    '0px 28px 56px rgba(0,0,0,0.1)',
    '0px 32px 64px rgba(0,0,0,0.1)',
    '0px 36px 72px rgba(0,0,0,0.1)',
    '0px 40px 80px rgba(0,0,0,0.1)',
    '0px 44px 88px rgba(0,0,0,0.1)',
    '0px 48px 96px rgba(0,0,0,0.1)',
    '0px 52px 104px rgba(0,0,0,0.1)',
    '0px 56px 112px rgba(0,0,0,0.1)',
    '0px 60px 120px rgba(0,0,0,0.1)',
    '0px 64px 128px rgba(0,0,0,0.1)',
    '0px 68px 136px rgba(0,0,0,0.1)',
    '0px 72px 144px rgba(0,0,0,0.1)',
    '0px 76px 152px rgba(0,0,0,0.1)',
    '0px 80px 160px rgba(0,0,0,0.1)',
    '0px 84px 168px rgba(0,0,0,0.1)',
    '0px 88px 176px rgba(0,0,0,0.1)',
    '0px 92px 184px rgba(0,0,0,0.1)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '0.95rem',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0,0,0,0.15)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0px 6px 12px rgba(0,0,0,0.2)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0px 8px 24px rgba(0,0,0,0.15)',
            transform: 'translateY(-2px)',
            transition: 'all 0.3s ease-in-out',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiPopover: {
      defaultProps: {
        PaperProps: {
          sx: {
            maxHeight: 'calc(100vh - 96px)',
            overflowY: 'auto',
          },
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        PaperProps: {
          sx: {
            maxHeight: 'calc(100vh - 96px)',
            overflowY: 'auto',
          },
        },
      },
    },
  },
});

// Inner component that uses useLocation (must be inside Router)
function AppRoutes() {
  const location = useLocation();
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const isFirstRender = useRef(true);
  const previousPathname = useRef(null);

  // Show loading screen only on initial page load (home page) and manual refresh
  // Not on route changes
  useEffect(() => {
    // Check if this is the first render (initial load or refresh)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      
      // Only show loading screen if we're on the home page initially
      // This prevents showing on product detail pages opened directly
      if (location.pathname === '/') {
        setShowLoadingScreen(true);
      } else {
        // If not on home page, don't show loading screen
        setShowLoadingScreen(false);
      }
      previousPathname.current = location.pathname;
      return;
    }

    // On subsequent renders (route changes), always hide loading screen
    if (previousPathname.current !== null && previousPathname.current !== location.pathname) {
      // This is a route change, not initial load or refresh
      setShowLoadingScreen(false);
      previousPathname.current = location.pathname;
    }
  }, [location.pathname]);
  
  // Log page views
  useEffect(() => {
    if (!showLoadingScreen && location.pathname) {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userRole = user?.role || 'GUEST';
        auditService.logPageView(location.pathname, userRole);
      } catch (error) {
        console.error('Failed to log page view:', error);
      }
    }
  }, [location.pathname, showLoadingScreen]);

  // Handle body overflow during loading
  useEffect(() => {
    if (showLoadingScreen) {
      document.body.classList.add('loading-active');
    } else {
      // Ensure class is removed when loading screen is hidden
      document.body.classList.remove('loading-active');
    }
    return () => {
      // Cleanup: always remove the class on unmount
      document.body.classList.remove('loading-active');
    };
  }, [showLoadingScreen]);

  const handleLoadingComplete = () => {
    setShowLoadingScreen(false);
  };

  return (
    <>
      {showLoadingScreen && <LoadingScreen onComplete={handleLoadingComplete} />}
      {/* Only render main content after loading screen completes to prevent thread blocking */}
      {!showLoadingScreen && (
        <>
          <ScrollToTop />
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Navbar />
            <Box component="main" sx={{ flexGrow: 1 }}>
              <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/products" element={<ProductsPage />} />
                  <Route path="/products/:id" element={<ProductDetailPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/wishlist" element={<WishlistPage />} />
                  <Route path="/orders" element={<OrdersPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/support" element={<SupportPage />} />
                  <Route path="/about" element={<AboutUsPage />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/CustomSol" element={<CustomSol />} />
                  <Route path="/Quality" element={<Quality />} />
                  <Route path="/ReturnRefund" element={<ReturnRefund />} />
                  <Route path="/contact" element={<ContactUsPage />} />
                  <Route path="/our-story" element={<OurStoryPage />} />
                  <Route path="/careers" element={<CareersPage />} />
                  <Route path="/terms" element={<TermsOfServicePage />} />
                  <Route path="/sitemap" element={<SitemapPage />} />
                  <Route path="/delete-account" element={<DeleteAccountPage />} />
                  
                  {/* Protected Routes */}
                  <Route path="/profile" element={<ProfilePage />} />
                  
                  {/* Admin Routes */}
                  <Route path="/admin/*" element={<AdminDashboard />} />
                  <Route path="/order-management" element={<OrderManagement />} />
                  
                  {/* Seller Routes */}
                  <Route path="/seller/*" element={<SellerDashboard />} />
                  
                  {/* 404 Route */}
                  <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Box>
            <Footer />
          </Box>
        </>
      )}
    </>
  );
}

// AppContent component that uses Redux hooks
function AppContent() {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector(state => state.auth);

  // Fetch wishlist count and load user cart when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      // Fetch wishlist count for customers and sellers (sellers can also shop)
      if (user?.role === 'CUSTOMER' || user?.role === 'SELLER') {
        dispatch(getWishlistItemsCount(user.id));
      }
      dispatch(loadUserCart(user.id));
    }
  }, [dispatch, isAuthenticated, user?.id, user?.role]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppRoutes />
      </Router>
      
      {/* Toast Notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastClassName="custom-toast"
      />
    </ThemeProvider>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
